/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const styles = cssMap({
	selectedIssuesLabel: {
		marginLeft: token('space.050'),
		fontWeight: token('font.weight.regular'),
		color: token('color.text.subtle'),
	},

	title: {
		marginLeft: token('space.050'),
	},
});

type IssueSelectorHeaderProps = {
	title: string;
	issueState: {
		selectedIssueKeys: string[];
		areAllIssuesSelected: boolean;
		totalIssueCount: number;
	};
	isExpanded: boolean;
	isDisabled: boolean;
	handleExpandCollapse: () => void;
	handleSelectAllCheckboxChange: () => void;
};

export const IssueSelectorHeader = ({
	title,
	issueState: { selectedIssueKeys, areAllIssuesSelected, totalIssueCount },
	isExpanded,
	isDisabled,
	handleExpandCollapse,
	handleSelectAllCheckboxChange,
}: IssueSelectorHeaderProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Inline alignBlock="center">
			<Checkbox
				aria-label={formatMessage(messages.selectAllCheckboxLabel)}
				testId="issue-selector-select-all-checkbox"
				isChecked={selectedIssueKeys.length > 0}
				isDisabled={isDisabled}
				isIndeterminate={!areAllIssuesSelected && selectedIssueKeys.length > 0}
				onChange={handleSelectAllCheckboxChange}
			/>
			<IconButton
				appearance="subtle"
				spacing="compact"
				icon={(iconProps) =>
					isExpanded ? (
						<ChevronDownIcon {...iconProps} size="small" />
					) : (
						<ChevronRightIcon {...iconProps} size="small" />
					)
				}
				label={formatMessage(
					isExpanded ? messages.collapseButtonLabelText : messages.expandButtonLabelText,
				)}
				onClick={handleExpandCollapse}
			/>
			<Box xcss={styles.title}>
				<Text weight="semibold">
					{title}
					<Box as="span" xcss={styles.selectedIssuesLabel}>
						{formatMessage(messages.selectedIssuesLabel, {
							issueCount: totalIssueCount,
							selectedIssueCount: selectedIssueKeys.length,
						})}
					</Box>
				</Text>
			</Box>
		</Inline>
	);
};

export const BulkIssueSelectorHeader = ({
	title,
	issueState: { selectedIssueKeys, areAllIssuesSelected, totalIssueCount },
	isDisabled,
	handleSelectAllCheckboxChange,
}: Omit<IssueSelectorHeaderProps, 'isExpanded' | 'handleExpandCollapse'>): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Inline alignBlock="center">
			<Checkbox
				aria-label={formatMessage(messages.selectAllCheckboxLabel)}
				testId="issue-selector-select-all-checkbox"
				isChecked={selectedIssueKeys.length > 0}
				isDisabled={isDisabled}
				isIndeterminate={!areAllIssuesSelected && selectedIssueKeys.length > 0}
				onChange={handleSelectAllCheckboxChange}
			/>
			<Box xcss={styles.title}>
				<Text weight="semibold">{title}</Text>
			</Box>
		</Inline>
	);
};
